import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';
import {
	CustomerDataUI,
	JourneyMetadataUI,
	RequestValidateCustomerData,
	ResponseValidateCustomerData,
} from 'src/app/interfaces/customer-data.interface';
import {
	GenerateSendOtpUI,
	RequestGenerateSendOtp,
	ResponseGenerateSendOtp,
} from 'src/app/interfaces/generate-send-otp.interface';
import {
	InitialParametersModel,
	InitialParametersUI,
} from 'src/app/interfaces/initial-parameters.interface';
import { OtpValidationUI, RequestOtpValidation } from 'src/app/interfaces/otp-validation.interface';
import { mapper } from 'src/app/utils/mapper';
import { ApiService } from '../api/api.service';

@Injectable({
	providedIn: 'root',
})
export class StoreService {
	private journeyMetadataSubject = new BehaviorSubject<JourneyMetadataUI | null>(null);
	readonly journeyMetadata$ = this.journeyMetadataSubject.asObservable();

	private initialParametersSubject = new BehaviorSubject<InitialParametersUI | null>(null);
	readonly initialParameters$ = this.initialParametersSubject.asObservable();

	private customerDataSubject = new BehaviorSubject<CustomerDataUI | null>(null);
	readonly customerData$ = this.customerDataSubject.asObservable();

	private otpSubject = new BehaviorSubject<GenerateSendOtpUI | null>(null);
	readonly otp$ = this.otpSubject.asObservable();

	constructor(private readonly apiService: ApiService) {}

	get journeyMetadata(): JourneyMetadataUI | null {
		return this.journeyMetadataSubject.value;
	}

	setCustomerData(customerData: CustomerDataUI): void {
		this.customerDataSubject.next(customerData);
	}

	initialParameters(product: string): Observable<InitialParametersModel> {
		return this.apiService.initialParameters(product).pipe(
			map(resp => mapper.initialParameters(resp)),
			tap(mapped => {
				this.journeyMetadataSubject.next({ productType: mapped.simulatorType });
				this.initialParametersSubject.next(this.extractSimulatorType(mapped));
			})
		);
	}

	validateData(req: RequestValidateCustomerData): Observable<ResponseValidateCustomerData> {
		return this.apiService.validateCustomerData(req).pipe(
			tap((response: ResponseValidateCustomerData) => {
				this.patchJourneyMetadata({ uuid: response.uuid, id: response.id });
			})
		);
	}

	generateSendOtp(
		req: RequestGenerateSendOtp,
		skipLoader: boolean = false
	): Observable<ResponseGenerateSendOtp> {
		return this.apiService.generateAndSendOtp(req, skipLoader).pipe(
			tap((response: ResponseGenerateSendOtp) => {
				if (req.nuevoDestino) this.updateCustomerPhone(req.nuevoDestino);
				this.otpSubject.next(mapper.otpGenerated(response));
			})
		);
	}

	validationOtp(req: RequestOtpValidation): Observable<OtpValidationUI> {
		return this.apiService.validateOtp(req).pipe(map(mapper.otpValidation));
	}

	private extractSimulatorType(parameters: InitialParametersModel): InitialParametersUI {
		const { simulatorType, ...parametersUI } = parameters;
		return parametersUI;
	}

	private updateCustomerPhone(newPhone: string): void {
		const currentCustomer = this.customerDataSubject.value;
		if (currentCustomer) {
			this.customerDataSubject.next({
				...currentCustomer,
				phoneNumber: newPhone,
			});
		}
	}

	private patchJourneyMetadata(patch: Partial<JourneyMetadataUI>): void {
		const current = this.journeyMetadataSubject.value;
		if (!current) return;

		this.journeyMetadataSubject.next({
			...current,
			...patch,
		});
	}
}
